import { useEffect, useRef } from 'react';

const MAX_DPR = 1.75;
const TARGET_FRAME_MS = 1000 / 38;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function gaussian(x: number, z: number, centerX: number, centerZ: number, radiusX: number, radiusZ: number) {
  const dx = (x - centerX) / radiusX;
  const dz = (z - centerZ) / radiusZ;
  return Math.exp(-(dx * dx + dz * dz));
}

type TerrainPoint = {
  x: number;
  y: number;
  elevation: number;
  peak: number;
  energy: number;
};

const HILLS = [
  { x: -2.82, z: 0.76, sx: 0.62, sz: 0.46, h: 0.82 },
  { x: -2.02, z: 1.72, sx: 0.92, sz: 0.78, h: 1.12 },
  { x: -1.12, z: 2.84, sx: 0.78, sz: 0.62, h: 1.08 },
  { x: -0.12, z: 2.06, sx: 1.12, sz: 0.82, h: 1.52 },
  { x: 0.88, z: 3.34, sx: 0.82, sz: 0.68, h: 0.96 },
  { x: 1.72, z: 1.48, sx: 0.76, sz: 0.58, h: 0.88 },
  { x: 2.58, z: 3.12, sx: 1.02, sz: 0.56, h: 1.06 },
  { x: 0.42, z: 4.42, sx: 2.16, sz: 0.34, h: 0.7 },
  { x: -1.84, z: 4.14, sx: 1.76, sz: 0.3, h: 0.48 },
];

const BASINS = [
  { x: -1.42, z: 0.96, sx: 0.5, sz: 0.38, h: -0.58 },
  { x: -0.14, z: 1.76, sx: 0.92, sz: 0.56, h: -0.66 },
  { x: 0.38, z: 3.18, sx: 1.12, sz: 0.74, h: -1.04 },
  { x: 1.58, z: 2.34, sx: 0.62, sz: 0.46, h: -0.76 },
  { x: 2.32, z: 1.08, sx: 0.46, sz: 0.34, h: -0.42 },
];

export function HeroWaveField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasNode = canvasRef.current;
    if (!canvasNode) return;

    const contextNode = canvasNode.getContext('2d', { alpha: true });
    if (!contextNode) return;

    const canvas: HTMLCanvasElement = canvasNode;
    const context: CanvasRenderingContext2D = contextNode;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let animationFrame = 0;
    let lastFrame = 0;
    let pointerX = 0;
    let pointerY = 0;
    let smoothPointerX = 0;
    let smoothPointerY = 0;
    let lightTheme = document.documentElement.dataset.theme === 'light';

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    function resize() {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function rgba(alpha: number, accent = false) {
      if (accent) return `rgba(255, 124, 96, ${alpha})`;
      return lightTheme ? `rgba(30, 24, 22, ${alpha})` : `rgba(255, 248, 243, ${alpha})`;
    }

    function terrainHeight(worldX: number, worldZ: number, t: number) {
      const driftA = Math.sin(t * 0.38) * 0.18;
      const driftB = Math.cos(t * 0.26) * 0.16;
      const driftC = Math.sin(t * 0.21 + 0.8) * 0.14;
      const driftD = Math.cos(t * 0.16 + 1.3) * 0.12;

      let elevation = 0;
      for (let i = 0; i < HILLS.length; i += 1) {
        const hill = HILLS[i];
        const dx = (i % 2 === 0 ? driftA : driftB) * (0.35 + i * 0.015);
        const dz = (i % 3 === 0 ? driftC : driftD) * (0.22 + i * 0.01);
        elevation += hill.h * gaussian(worldX, worldZ, hill.x + dx, hill.z + dz, hill.sx, hill.sz);
      }
      for (let i = 0; i < BASINS.length; i += 1) {
        const basin = BASINS[i];
        const dx = (i % 2 === 0 ? driftD : driftA) * (0.3 + i * 0.02);
        const dz = (i % 2 === 1 ? driftB : driftC) * (0.18 + i * 0.016);
        elevation += basin.h * gaussian(worldX, worldZ, basin.x + dx, basin.z + dz, basin.sx, basin.sz);
      }

      const ridgeA = Math.sin(worldX * 1.2 - worldZ * 0.9 + t * 1.08) * 0.14;
      const ridgeB = Math.cos(worldX * 0.76 + worldZ * 1.34 - t * 0.78) * 0.11;
      const ridgeC = Math.sin(worldX * 2.9 + worldZ * 0.52 + t * 0.64) * 0.07;
      const warp = Math.cos(worldX * 2.2 - worldZ * 1.8 + t * 0.6) * 0.06;
      const fine = Math.sin(worldX * 6.2 + worldZ * 1.3 - t * 1.24) * 0.022;

      elevation += ridgeA + ridgeB + ridgeC + fine;

      const peak = clamp((elevation + 0.35) / 1.95, 0, 1);
      const energy = ridgeA * 0.7 + ridgeB * 0.5 + ridgeC * 0.45 + warp;
      return { elevation, peak, energy };
    }

    function draw(timeMs: number) {
      const reduced = reducedMotionQuery.matches || document.documentElement.dataset.reducedMotion === 'true';
      const t = reduced ? 0 : timeMs * 0.00023;

      context.clearRect(0, 0, width, height);
      smoothPointerX += (pointerX - smoothPointerX) * 0.034;
      smoothPointerY += (pointerY - smoothPointerY) * 0.034;

      const horizon = height * 0.13;
      const bottom = height * 1.04;
      const rows = clamp(Math.round(height / 8.2), 60, 88);
      const columns = clamp(Math.round(width / 10.0), 96, 160);
      const grid: TerrainPoint[][] = [];

      for (let row = 0; row < rows; row += 1) {
        const depth = row / (rows - 1);
        const perspective = Math.pow(depth, 1.46);
        const baseY = horizon + (bottom - horizon) * perspective;
        const amplitude = 8 + perspective * height * 0.115;
        const rowWave = Math.sin(depth * 9.4 + t * 1.04) * (3 + perspective * 15);
        const rowPoints: TerrainPoint[] = [];

        for (let column = 0; column < columns; column += 1) {
          const u = column / (columns - 1);
          const centeredX = u * 2 - 1;
          const worldZ = depth * 5.15;
          const domainWarp = Math.sin(worldZ * 0.9 + t * 0.7) * 0.28 + Math.cos(centeredX * 1.4 + t * 0.38) * 0.09;
          const worldX = centeredX * 3.7 + domainWarp + smoothPointerX * (0.08 + depth * 0.2);
          const { elevation, peak, energy } = terrainHeight(worldX, worldZ, t);

          const widthFactor = width * (0.58 + perspective * 0.50);
          const lateralWarp = Math.sin(worldZ * 1.38 + centeredX * 3.1 + t * 0.52) * (5 + perspective * 14);
          const edgeBow = Math.sin(centeredX * Math.PI * 0.9) * Math.pow(perspective, 1.15) * 34;
          const x = width * 0.5 + centeredX * widthFactor + lateralWarp + edgeBow + elevation * (5 + perspective * 12) + smoothPointerX * (14 + depth * 22);

          const floorUndulation = (
            Math.sin(centeredX * 3.4 + t * 0.26) +
            Math.sin(centeredX * 6.8 - t * 0.19) * 0.48 +
            Math.cos(centeredX * 9.4 + t * 0.16) * 0.22
          ) * Math.pow(perspective, 1.35) * 14;

          const y =
            baseY - elevation * amplitude +
            rowWave * (0.22 + peak * 0.28) +
            energy * (5 + perspective * 9) +
            Math.sin(centeredX * 6.8 + t * 0.45 + depth * 8.5) * (1.4 + perspective * 2.8) +
            floorUndulation +
            smoothPointerY * (8 + perspective * 14);

          rowPoints.push({ x, y, elevation, peak, energy });
        }

        grid.push(rowPoints);
      }

      context.lineCap = 'round';
      context.lineJoin = 'round';

      for (let row = 0; row < rows; row += 1) {
        const depth = row / (rows - 1);
        const points = grid[row];
        const near = Math.pow(depth, 0.85);

        context.beginPath();
        for (let column = 0; column < points.length; column += 1) {
          const point = points[column];
          if (column === 0) context.moveTo(point.x, point.y);
          else context.lineTo(point.x, point.y);
        }
        context.strokeStyle = rgba(0.006 + near * 0.082);
        context.lineWidth = 0.62 + near * 0.68;
        context.stroke();

        let active = false;
        context.beginPath();
        for (let column = 0; column < points.length; column += 1) {
          const point = points[column];
          if (point.peak > 0.54 || (point.peak > 0.42 && point.energy > 0.06)) {
            if (!active) {
              context.moveTo(point.x, point.y);
              active = true;
            } else {
              context.lineTo(point.x, point.y);
            }
          } else if (active) {
            context.strokeStyle = rgba(0.014 + near * 0.12);
            context.lineWidth = 0.84 + near * 0.74;
            context.stroke();
            context.beginPath();
            active = false;
          }
        }
        if (active) {
          context.strokeStyle = rgba(0.014 + near * 0.12);
          context.lineWidth = 0.84 + near * 0.74;
          context.stroke();
        }
      }

      for (let row = 5; row < rows; row += 3) {
        const depth = row / (rows - 1);
        const points = grid[row];
        for (let column = row % 6; column < columns; column += 6) {
          const point = points[column];
          if (point.peak < 0.46) continue;
          const radius = 0.24 + depth * 0.48 + point.peak * 0.15;
          context.beginPath();
          context.arc(point.x, point.y, radius, 0, Math.PI * 2);
          context.fillStyle = rgba(0.01 + depth * 0.04 + point.peak * 0.03);
          context.fill();
        }
      }

      const glow = context.createRadialGradient(width * 0.54, height * 0.42, 0, width * 0.54, height * 0.42, Math.max(width, height) * 0.48);
      glow.addColorStop(0, 'rgba(255, 124, 96, 0.045)');
      glow.addColorStop(0.34, 'rgba(255, 124, 96, 0.02)');
      glow.addColorStop(1, 'rgba(255, 124, 96, 0)');
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);
    }

    function frame(now: number) {
      if (now - lastFrame >= TARGET_FRAME_MS) {
        lastFrame = now;
        draw(now);
      }
      animationFrame = window.requestAnimationFrame(frame);
    }

    function onPointerMove(event: PointerEvent) {
      const rect = canvas.getBoundingClientRect();
      pointerX = ((event.clientX - rect.left) / Math.max(1, rect.width) - 0.5) * 2;
      pointerY = ((event.clientY - rect.top) / Math.max(1, rect.height) - 0.5) * 2;
    }

    function onPointerLeave() {
      pointerX = 0;
      pointerY = 0;
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const themeObserver = new MutationObserver(() => {
      lightTheme = document.documentElement.dataset.theme === 'light';
      if (reducedMotionQuery.matches || document.documentElement.dataset.reducedMotion === 'true') draw(0);
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-reduced-motion'],
    });

    canvas.addEventListener('pointermove', onPointerMove, { passive: true });
    canvas.addEventListener('pointerleave', onPointerLeave, { passive: true });

    resize();
    draw(0);
    animationFrame = window.requestAnimationFrame(frame);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      themeObserver.disconnect();
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
    };
  }, []);

  return (
    <div className="hero-wave-field" aria-hidden="true">
      <canvas ref={canvasRef} className="hero-wave-canvas" />
      <div className="hero-wave-horizon" />
      <div className="hero-wave-vignette" />
    </div>
  );
}
